import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { BaseFields, PresenceFields, PipelineFields } from "./form/ProspectFormFields";
import { createProspect, updateProspect } from "../lib/api";
import { toast } from "sonner";

const EMPTY = {
  azienda: "", settore: "", citta: "", telefono: "", email: "",
  instagram: "", facebook: "", tiktok: "", sito: "", referente: "", note: "",
  digital_presence: { instagram: 0, facebook: 0, tiktok: 0, website: 0, google: 0, advertising: 0, branding: 0, content: 0 },
  ig_stats: { followers: "", following: "", posts: "", posts_per_week: "", avg_likes: "", avg_comments: "" },
  problema_principale: "", opportunita: "", servizio_consigliato: "", budget_potenziale: "",
  valore_potenziale: 0, status: "NEW", responsabile: "", next_action: "", next_action_date: "",
};

const fromProspect = (p) => (p
  ? { ...EMPTY, ...p, digital_presence: { ...EMPTY.digital_presence, ...(p.digital_presence || {}) }, ig_stats: { ...EMPTY.ig_stats, ...(p.ig_stats || {}) } }
  : EMPTY);

const numericIgStats = (ig) => Object.fromEntries(
  Object.entries(ig || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([k, v]) => [k, Number(v)]),
);

export default function ProspectForm({ open, onOpenChange, prospect, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!prospect;

  useEffect(() => {
    if (open) setForm(fromProspect(prospect));
  }, [open, prospect]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDp = (k, v) => setForm((f) => ({ ...f, digital_presence: { ...f.digital_presence, [k]: v } }));
  const setIg = (k, v) => setForm((f) => ({ ...f, ig_stats: { ...f.ig_stats, [k]: v } }));

  const save = async () => {
    if (!form.azienda.trim()) { toast.error("Il nome azienda è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form, valore_potenziale: Number(form.valore_potenziale) || 0, ig_stats: numericIgStats(form.ig_stats) };
      const res = isEdit ? await updateProspect(prospect.id, payload) : await createProspect(payload);
      toast.success(isEdit ? "Prospect aggiornato" : "Prospect creato");
      onSaved && onSaved(res);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-card" data-testid="prospect-form">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Modifica Prospect" : "Nuovo Prospect"}</DialogTitle>
          <DialogDescription className="sr-only">Compila i dati del prospect nelle tre schede.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="base" className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="base" data-testid="tab-base">Dati Base</TabsTrigger>
            <TabsTrigger value="presence" data-testid="tab-presence">Presenza Digitale</TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="tab-pipeline">Opportunità</TabsTrigger>
          </TabsList>
          <TabsContent value="base" className="space-y-4 pt-4"><BaseFields form={form} set={set} /></TabsContent>
          <TabsContent value="presence" className="space-y-5 pt-4"><PresenceFields form={form} setDp={setDp} setIg={setIg} /></TabsContent>
          <TabsContent value="pipeline" className="space-y-4 pt-4"><PipelineFields form={form} set={set} /></TabsContent>
        </Tabs>

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="save-prospect-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
